import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import type { User } from "@overmap-ai/common"
import { useSort, useTextFilter } from "@overmap-ai/hooks"
import { memo, type ReactElement } from "react"

import { userFilterCallback } from "../../filtering/utils"
import { modelIconMapping } from "../../mappings/icons"
import { userSortFn } from "../../sorting/utils"
import { UserSelectGroup, type UserSelectGroupProps } from "../MenuSelectGroups/UserSelectGroup"

type UserSelectProps = Omit<MenuRootProps, "children"> &
	Omit<MenuClickTriggerProps, "children"> &
	MenuVariantProps &
	UserSelectGroupProps & {
		children: ReactElement
	}

export const UserSelect = memo<UserSelectProps>((props) => {
	const {
		// Trigger props
		children,
		disabled = false,
		// MenuContent props
		size,
		radius,
		variant,
		accentColor,
		// UserSelectGroup props
		users,
		required,
		value,
		defaultValue,
		onValueChange,
		// Rest props
		...rest
	} = props

	const sortedUsers = useSort(users, userSortFn)

	const [filteredUsers, filterValue, setFilterValue] = useTextFilter<User>(sortedUsers, userFilterCallback)

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} variant={variant} accentColor={accentColor}>
				<Menu.InputRoot>
					<Menu.InputSlot>
						<LuIcon icon={modelIconMapping.search} />
					</Menu.InputSlot>
					<Menu.InputField placeholder="Search" value={filterValue} onValueChange={setFilterValue} />
				</Menu.InputRoot>
				{filteredUsers.length > 0 && <Menu.Separator />}
				<Menu.Scroll>
					{required ? (
						<UserSelectGroup
							required={required}
							value={value}
							defaultValue={defaultValue}
							onValueChange={onValueChange}
							users={filteredUsers}
						/>
					) : (
						<UserSelectGroup
							required={required}
							value={value}
							defaultValue={defaultValue}
							onValueChange={onValueChange}
							users={filteredUsers}
						/>
					)}
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
UserSelect.displayName = "UserSelect"
